import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Package,
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  AlertTriangle,
  XCircle,
  QrCode,
  X,
  Building2,
  MapPin,
  Building,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  FileSpreadsheet,
  FileText,
  ChevronDown
} from 'lucide-react';
import { supplierService } from '../../../services/supplierService';
import { branchService } from '../../../services/branchService';
import { locationService } from '../../../services/locationService';
import { assetService } from '../../../services/assetService';
import { useCmmsAuth } from '../../../contexts/CmmsAuthContext';
import { PERMISSIONS } from '../../../constants/permissions';
import { exportToCSV, exportToExcel, formatCurrencyForExport, getDateString } from '../../../utils/exportUtils';
import { useTranslation } from 'react-i18next';
import UsageMeter from '../../../components/UsageMeter';
import OverageConsentModal from '../../../components/OverageConsentModal';
import { subscriptionService } from '../../../services/subscriptionService';

const Assets = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const editTriggered = useRef(false);
  // Permission-based controls
  const { hasPermission } = useCmmsAuth();
  const isCompanyOwner = localStorage.getItem('companyRole') === 'COMPANY_OWNER';
  const canCreate = isCompanyOwner || hasPermission(PERMISSIONS.CREATE_ASSETS);
  const canEdit = isCompanyOwner || hasPermission(PERMISSIONS.EDIT_ASSETS);
  const canDelete = isCompanyOwner || hasPermission(PERMISSIONS.DELETE_ASSETS);
  const [viewMode, setViewMode] = useState('table');
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [showNewSupplierForm, setShowNewSupplierForm] = useState(false);
  const [savingSupplier, setSavingSupplier] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ name: '', contactPerson: '', email: '', phone: '' });

  const [formData, setFormData] = useState({
    name: '',
    assetTag: '',
    category: '',
    type: '',
    manufacturer: '',
    model: '',
    serialNumber: '',
    supplierId: '',
    branchId: '',
    locationId: '',
    status: 'OPERATIONAL',
    criticality: 'MEDIUM',
    purchaseDate: '',
    purchaseCost: '',
    warrantyExpiration: '',
    warrantyProvider: '',
    description: '',
    notes: ''
  });

  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sortBy] = useState('createdAt');
  const [sortDir] = useState('desc');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [cmmsSubscription, setCmmsSubscription] = useState(null);
  const [showOverageModal, setShowOverageModal] = useState(false);

  // Export handler
  const handleExport = (format) => {
    const data = assets.map(a => ({
      assetTag: a.assetTag || '',
      name: a.name || '',
      category: a.category || '',
      status: a.status || '',
      branchName: a.branchName || '',
      locationName: a.locationName || '',
      supplierName: a.supplierName || '',
      manufacturer: a.manufacturer || '',
      model: a.model || '',
      serialNumber: a.serialNumber || '',
      purchaseDate: a.purchaseDate || '',
      purchaseCost: formatCurrencyForExport(a.purchaseCost),
      warrantyExpiration: a.warrantyExpiration || '',
    }));

    const columns = [
      { key: 'assetTag', label: 'Asset Tag' },
      { key: 'name', label: 'Name' },
      { key: 'category', label: 'Category' },
      { key: 'status', label: 'Status' },
      { key: 'branchName', label: 'Branch' },
      { key: 'locationName', label: 'Location' },
      { key: 'supplierName', label: 'Supplier' },
      { key: 'manufacturer', label: 'Manufacturer' },
      { key: 'model', label: 'Model' },
      { key: 'serialNumber', label: 'Serial Number' },
      { key: 'purchaseDate', label: 'Purchase Date' },
      { key: 'purchaseCost', label: 'Purchase Cost' },
      { key: 'warrantyExpiration', label: 'Warranty Expiration' },
    ];

    const filename = `assets-${getDateString()}`;
    if (format === 'csv') {
      exportToCSV(data, columns, filename);
    } else {
      exportToExcel(data, columns, filename);
    }
    setShowExportMenu(false);
  };

  useEffect(() => {
    loadReferenceData();
  }, []);

  useEffect(() => {
    subscriptionService.getCmmsSubscription()
      .then(data => setCmmsSubscription(data))
      .catch(() => {});
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(0); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadAssets();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- helper identity changes each render; dependency list is intentional
  }, [currentPage, pageSize, sortBy, sortDir, filterStatus, filterBranch, filterLocation, debouncedSearch]);

  // Auto-open edit modal when arrived from asset detail via ?edit=<id>
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || loading || loadingSuppliers || editTriggered.current) return;
    editTriggered.current = true;
    assetService.getAssetById(editId)
      .then(asset => { if (asset) handleEdit(asset); })
      .catch(() => {});
  }, [loading, loadingSuppliers]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReferenceData = async () => {
    setLoadingSuppliers(true);
    try {
      const [suppliersData, branchesData, locationsData] = await Promise.all([
        supplierService.getSuppliers(),
        branchService.getBranches(),
        locationService.getLocations()
      ]);
      setSuppliers(suppliersData || []);
      setBranches(branchesData || []);
      setLocations(locationsData || []);
    } catch (error) {
      console.error('Error loading reference data:', error);
      setSuppliers([]);
      setBranches([]);
      setLocations([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const loadAssets = async () => {
    setLoading(true);
    try {
      const response = await assetService.getAssetsPaginated({
        page: currentPage,
        size: pageSize,
        sortBy,
        sortDir,
        status: filterStatus !== 'all' ? filterStatus : null,
        branchId: filterBranch !== 'all' ? filterBranch : null,
        locationId: filterLocation !== 'all' ? filterLocation : null,
        search: debouncedSearch || null
      });
      setAssets(response.content || []);
      setTotalElements(response.totalElements || 0);
      setTotalPages(response.totalPages || 0);
    } catch (error) {
      console.error('Error loading assets:', error);
      setAssets([]);
      setTotalElements(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  // Get locations filtered by selected branch (for form)
  const getFilteredLocations = () => {
    if (!formData.branchId) return [];
    return locations.filter(l => l.branchId === parseInt(formData.branchId));
  };

  // Get locations filtered by selected branch filter (for filter dropdown)
  const getFilterLocations = () => {
    if (filterBranch === 'all') return locations;
    return locations.filter(l => l.branchId === parseInt(filterBranch));
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      OPERATIONAL: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: t('cmms.asset_operational') },
      MAINTENANCE: { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle, label: t('cmms.asset_maintenance') },
      DOWN: { color: 'bg-red-100 text-red-800', icon: XCircle, label: t('cmms.asset_down') },
      RETIRED: { color: 'bg-gray-100 text-gray-800', icon: Package, label: t('cmms.asset_retired') }
    };
    return statusMap[status] || statusMap.OPERATIONAL;
  };

  const handleSelectAll = () => {
    if (selectedAssets.length === assets.length) {
      setSelectedAssets([]);
    } else {
      setSelectedAssets(assets.map(a => a.id));
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setSelectedAssets([]);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
    setSelectedAssets([]);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    // Page reset is handled in the debounce effect
  };

  const handleStatusFilterChange = (e) => {
    setFilterStatus(e.target.value);
    setCurrentPage(0);
  };

  const handleBranchFilterChange = (e) => {
    setFilterBranch(e.target.value);
    setFilterLocation('all'); // Reset location when branch changes
    setCurrentPage(0);
  };

  const handleLocationFilterChange = (e) => {
    setFilterLocation(e.target.value);
    setCurrentPage(0);
  };

  const handleSelectAsset = (id) => {
    if (selectedAssets.includes(id)) {
      setSelectedAssets(selectedAssets.filter(a => a !== id));
    } else {
      setSelectedAssets([...selectedAssets, id]);
    }
  };

  const handleCreate = () => {
    setEditingAsset(null);
    setFormData({
      name: '',
      assetTag: '',
      category: '',
      type: '',
      manufacturer: '',
      model: '',
      serialNumber: '',
      supplierId: '',
      branchId: '',
      locationId: '',
      status: 'OPERATIONAL',
      criticality: 'MEDIUM',
      purchaseDate: '',
      purchaseCost: '',
      warrantyExpiration: '',
      warrantyProvider: '',
      description: '',
      notes: ''
    });
    setShowNewSupplierForm(false);
    setNewSupplier({ name: '', contactPerson: '', email: '', phone: '' });
    const current = assets.length;
    const max = cmmsSubscription?.includedAssets ?? null;
    if (max !== null && current >= max) {
      setShowOverageModal(true);
    } else {
      setShowModal(true);
    }
  };

  const handleEdit = (asset) => {
    setShowNewSupplierForm(false);
    setNewSupplier({ name: '', contactPerson: '', email: '', phone: '' });
    setEditingAsset(asset);
    setFormData({
      name: asset.name || '',
      assetTag: asset.assetTag || '',
      category: asset.category || '',
      type: asset.type || '',
      manufacturer: asset.manufacturer || '',
      model: asset.model || '',
      serialNumber: asset.serialNumber || '',
      supplierId: asset.supplierId || '',
      branchId: asset.branchId || '',
      locationId: asset.locationId || '',
      status: asset.status || 'OPERATIONAL',
      criticality: asset.criticality || 'MEDIUM',
      purchaseDate: asset.purchaseDate || '',
      purchaseCost: asset.purchaseCost || '',
      warrantyExpiration: asset.warrantyExpiration || '',
      warrantyProvider: asset.warrantyProvider || '',
      description: asset.description || '',
      notes: asset.notes || ''
    });
    setShowModal(true);
  };

  const handleSupplierChange = (e) => {
    const val = e.target.value;
    if (val === '__new__') {
      setShowNewSupplierForm(true);
      setFormData({ ...formData, supplierId: '' });
    } else {
      setShowNewSupplierForm(false);
      setFormData({ ...formData, supplierId: val });
    }
  };

  const handleCreateSupplier = async () => {
    if (!newSupplier.name.trim()) return;
    setSavingSupplier(true);
    try {
      const created = await supplierService.createSupplier(newSupplier);
      setSuppliers(prev => [...prev, created]);
      setFormData(prev => ({ ...prev, supplierId: String(created.id) }));
      setShowNewSupplierForm(false);
      setNewSupplier({ name: '', contactPerson: '', email: '', phone: '' });
    } catch (err) {
      console.error('Error creating supplier:', err);
      alert('Failed to create supplier.');
    } finally {
      setSavingSupplier(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAsset) {
        await assetService.updateAsset(editingAsset.id, formData);
      } else {
        await assetService.createAsset(formData);
        subscriptionService.getCmmsSubscription()
          .then(data => setCmmsSubscription(data))
          .catch(() => {});
      }
      setShowModal(false);
      loadAssets();
    } catch (error) {
      console.error('Error saving asset:', error);
      alert('Failed to save asset. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this asset?')) {
      try {
        await assetService.deleteAsset(id);
        loadAssets();
      } catch (error) {
        console.error('Error deleting asset:', error);
        alert('Failed to delete asset. Please try again.');
      }
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  // Stats (based on current page data - for full stats, a separate API call would be needed)
  const stats = {
    total: totalElements,
    operational: assets.filter(a => a.status === 'OPERATIONAL').length,
    maintenance: assets.filter(a => a.status === 'MAINTENANCE').length,
    down: assets.filter(a => a.status === 'DOWN').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('cmms.asset_title')}</h1>
          <p className="text-gray-600 mt-1">{t('cmms.asset_subtitle')}</p>
        </div>
        <div className="flex gap-3">
          {/* Export Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
            >
              <Download className="w-5 h-5 mr-2" />
              {t('common.export')}
              <ChevronDown className="w-4 h-4 ml-2" />
            </button>
            {showExportMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                  <button
                    onClick={() => handleExport('excel')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-green-600" />
                    {t('common.exportExcel')}
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4 text-blue-600" />
                    {t('common.exportCsv')}
                  </button>
                </div>
              </>
            )}
          </div>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50">
            <Upload className="w-5 h-5 mr-2" />
            {t('common.import')}
          </button>
          {canCreate && (
            <button
              onClick={handleCreate}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('cmms.asset_addAsset')}
            </button>
          )}
        </div>
      </div>

      {cmmsSubscription && (
        <UsageMeter
          label={t('cmms.assets')}
          current={assets.length}
          max={cmmsSubscription.includedAssets ?? null}
          className="mb-4"
        />
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-sm text-gray-600">{t('cmms.totalAssets')}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-green-600">{stats.operational}</div>
          <div className="text-sm text-gray-600">{t('cmms.asset_operational')}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-yellow-600">{stats.maintenance}</div>
          <div className="text-sm text-gray-600">{t('cmms.asset_maintenance')}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-red-600">{stats.down}</div>
          <div className="text-sm text-gray-600">{t('cmms.asset_down')}</div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <select
            value={filterBranch}
            onChange={handleBranchFilterChange}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('common.branch')}</option>
            {branches.map(branch => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
          <select
            value={filterLocation}
            onChange={handleLocationFilterChange}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('cmms.asset_allLocations')}</option>
            {getFilterLocations().map(location => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={filterStatus}
              onChange={handleStatusFilterChange}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{t('common.allStatus')}</option>
              <option value="OPERATIONAL">{t('cmms.asset_operational')}</option>
              <option value="MAINTENANCE">{t('cmms.asset_maintenance')}</option>
              <option value="DOWN">{t('cmms.asset_down')}</option>
              <option value="RETIRED">{t('cmms.asset_retired')}</option>
            </select>
          </div>
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1 rounded ${viewMode === 'table' ? 'bg-white shadow-sm' : ''}`}
            >
              {t('common.table')}
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
            >
              {t('common.grid')}
            </button>
          </div>
        </div>

        {selectedAssets.length > 0 && (
          <div className="mt-4 flex items-center gap-4 p-3 bg-blue-50 rounded-lg">
            <span className="text-sm font-medium text-gray-700">
              {selectedAssets.length} {t('cmms.asset_selected')}
            </span>
            <div className="flex gap-2">
              <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                {t('cmms.asset_bulkEdit')}
              </button>
              <button className="text-sm text-red-600 hover:text-red-700 font-medium">
                {t('cmms.asset_deleteSelected')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Assets Table */}
      {viewMode === 'table' ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedAssets.length === assets.length && assets.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.name')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.branch')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.asset_location')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.asset_supplier')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.asset_nextMaintenance')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {assets.map((asset) => {
                  const statusBadge = getStatusBadge(asset.status);
                  const StatusIcon = statusBadge.icon;
                  return (
                    <tr key={asset.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedAssets.includes(asset.id)}
                          onChange={() => handleSelectAsset(asset.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Package className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{asset.name}</div>
                            <div className="text-xs text-gray-500">{asset.assetTag} • {asset.serialNumber}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {asset.branchName ? (
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-900">{asset.branchName}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {asset.locationName ? (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-900">{asset.locationName}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {asset.supplierName ? (
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <div>
                              <div className="text-sm text-gray-900">{asset.supplierName}</div>
                              <div className="text-xs text-gray-500">{asset.supplierCode}</div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusBadge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {asset.nextMaintenance || '-'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/portal/cmms/assets/${asset.id}`}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          >
                            <Eye className="w-4 h-4" />
                          </Link>
                          {canEdit && (
                            <button
                              onClick={() => handleEdit(asset)}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          )}
                          <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                            <QrCode className="w-4 h-4" />
                          </button>
                          {canDelete && (
                            <button
                              onClick={() => handleDelete(asset.id)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                {t('cmms.asset_showingOf', {from: currentPage * pageSize + 1, to: Math.min((currentPage + 1) * pageSize, totalElements), total: totalElements})}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{t('cmms.rowsPerPage')}</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(0)}
                disabled={currentPage === 0}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                Page {currentPage + 1} of {totalPages || 1}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages - 1)}
                disabled={currentPage >= totalPages - 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Grid View */
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {assets.map((asset) => {
            const statusBadge = getStatusBadge(asset.status);
            const StatusIcon = statusBadge.icon;
            return (
              <div key={asset.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Package className="w-6 h-6 text-blue-600" />
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge.color}`}>
                    <StatusIcon className="w-3 h-3" />
                    {statusBadge.label}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{asset.name}</h3>
                <p className="text-sm text-gray-500 mb-4">{asset.assetTag}</p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{t('cmms.asset_category')}:</span>
                    <span className="font-medium text-gray-900">{asset.category}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center gap-1">
                      <Building className="w-3 h-3" />
                      {t('common.branch')}:
                    </span>
                    <span className="font-medium text-gray-900 text-right">{asset.branchName || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {t('cmms.asset_location')}:
                    </span>
                    <span className="font-medium text-gray-900 text-right">{asset.locationName || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{t('cmms.asset_supplier')}:</span>
                    <span className="font-medium text-gray-900">{asset.supplierName || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{t('cmms.inv_value')}:</span>
                    <span className="font-medium text-gray-900">{formatCurrency(asset.purchaseCost)}</span>
                  </div>
                </div>
                <div className="flex gap-2 pt-4 border-t border-gray-200">
                  <Link
                    to={`/portal/cmms/assets/${asset.id}`}
                    className={`${canEdit ? 'flex-1' : 'w-full'} inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50`}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {t('common.view')}
                  </Link>
                  {canEdit && (
                    <button
                      onClick={() => handleEdit(asset)}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      {t('common.edit')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Grid View Pagination */}
      {viewMode === 'grid' && totalPages > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600">
              {t('cmms.asset_showingOf', {from: currentPage * pageSize + 1, to: Math.min((currentPage + 1) * pageSize, totalElements), total: totalElements})}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{t('cmms.rowsPerPage')}</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value={6}>6</option>
                <option value={12}>12</option>
                <option value={24}>24</option>
                <option value={48}>48</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(0)}
              disabled={currentPage === 0}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 py-1 text-sm text-gray-700">
              Page {currentPage + 1} of {totalPages || 1}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => handlePageChange(totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingAsset ? t('common.edit') : t('cmms.asset_addAsset')}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.asset_assetTag')} *</label>
                  <input
                    type="text"
                    value={formData.assetTag}
                    onChange={(e) => setFormData({...formData, assetTag: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder="AST-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.name')} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.asset_category')}</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('cmms.asset_selectCategory')}</option>
                    <option value="EQUIPMENT">Equipment</option>
                    <option value="VEHICLE">Vehicle</option>
                    <option value="BUILDING">Building</option>
                    <option value="IT">IT</option>
                    <option value="FURNITURE">Furniture</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.status')}</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="OPERATIONAL">{t('cmms.asset_operational')}</option>
                    <option value="MAINTENANCE">{t('cmms.asset_maintenance')}</option>
                    <option value="DOWN">{t('cmms.asset_down')}</option>
                    <option value="RETIRED">{t('cmms.asset_retired')}</option>
                  </select>
                </div>
              </div>

              {/* Location Section */}
              <div className="bg-green-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-green-900 mb-2">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  {t('cmms.asset_assetLocation')}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-green-700 mb-1">{t('cmms.asset_selectBranch')} *</label>
                    <select
                      value={formData.branchId}
                      onChange={(e) => setFormData({...formData, branchId: e.target.value, locationId: ''})}
                      className="w-full px-3 py-2 border border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 bg-white"
                    >
                      <option value="">{t('cmms.asset_selectBranch')}</option>
                      {branches.map(branch => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name} ({branch.code})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-green-700 mb-1">{t('cmms.asset_location')}</label>
                    <select
                      value={formData.locationId}
                      onChange={(e) => setFormData({...formData, locationId: e.target.value})}
                      className="w-full px-3 py-2 border border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 bg-white"
                      disabled={!formData.branchId}
                    >
                      <option value="">{t('cmms.asset_selectLocation')}</option>
                      {getFilteredLocations().map(location => (
                        <option key={location.id} value={location.id}>
                          {location.name} ({location.code})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="text-xs text-green-600 mt-2">
                  Select where this asset is physically located
                </p>
              </div>

              {/* Supplier Selection */}
              <div className="bg-blue-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-blue-900 mb-2">
                  <Building2 className="w-4 h-4 inline mr-2" />
                  {t('cmms.asset_supplierPurchasedFrom')}
                </label>
                <select
                  value={showNewSupplierForm ? '__new__' : formData.supplierId}
                  onChange={handleSupplierChange}
                  className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">{t('cmms.asset_selectSupplier')}</option>
                  {suppliers.map(supplier => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name} ({supplier.code})
                    </option>
                  ))}
                  <option value="__new__">+ Add New Supplier</option>
                </select>
                {showNewSupplierForm && (
                  <div className="mt-3 p-3 bg-white border border-blue-200 rounded-lg space-y-2">
                    <p className="text-xs font-medium text-blue-800 mb-2">New Supplier</p>
                    <input
                      type="text"
                      placeholder="Supplier name *"
                      value={newSupplier.name}
                      onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      placeholder="Contact person"
                      value={newSupplier.contactPerson}
                      onChange={(e) => setNewSupplier({ ...newSupplier, contactPerson: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="email"
                        placeholder="Email"
                        value={newSupplier.email}
                        onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="Phone"
                        value={newSupplier.phone}
                        onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        type="button"
                        onClick={handleCreateSupplier}
                        disabled={savingSupplier || !newSupplier.name.trim()}
                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                      >
                        {savingSupplier ? 'Saving...' : 'Save Supplier'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowNewSupplierForm(false); setNewSupplier({ name: '', contactPerson: '', email: '', phone: '' }); }}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                <p className="text-xs text-blue-600 mt-1">
                  Select the vendor who supplied this asset
                </p>
              </div>

              {/* Manufacturer Info */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.asset_manufacturer')}</label>
                  <input
                    type="text"
                    value={formData.manufacturer}
                    onChange={(e) => setFormData({...formData, manufacturer: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.asset_model')}</label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({...formData, model: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.asset_serialNumber')}</label>
                  <input
                    type="text"
                    value={formData.serialNumber}
                    onChange={(e) => setFormData({...formData, serialNumber: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Financial Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.asset_purchaseDate')}</label>
                  <input
                    type="date"
                    value={formData.purchaseDate}
                    onChange={(e) => setFormData({...formData, purchaseDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.asset_purchaseCost')}</label>
                  <input
                    type="number"
                    value={formData.purchaseCost}
                    onChange={(e) => setFormData({...formData, purchaseCost: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Warranty Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.asset_warrantyExpiry')}</label>
                  <input
                    type="date"
                    value={formData.warrantyExpiration}
                    onChange={(e) => setFormData({...formData, warrantyExpiration: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.asset_warrantyProvider')}</label>
                  <input
                    type="text"
                    value={formData.warrantyProvider}
                    onChange={(e) => setFormData({...formData, warrantyProvider: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.notes')}</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <OverageConsentModal
        isOpen={showOverageModal}
        onClose={() => setShowOverageModal(false)}
        resourceLabel="asset"
        current={assets.length}
        max={cmmsSubscription?.includedAssets ?? 0}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.asset_assetTag')} *</label>
              <input
                type="text"
                value={formData.assetTag}
                onChange={(e) => setFormData({...formData, assetTag: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
                placeholder="AST-001"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.name')} *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.asset_category')}</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('cmms.asset_selectCategory')}</option>
                <option value="EQUIPMENT">Equipment</option>
                <option value="VEHICLE">Vehicle</option>
                <option value="BUILDING">Building</option>
                <option value="IT">IT</option>
                <option value="FURNITURE">Furniture</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.status')}</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="OPERATIONAL">{t('cmms.asset_operational')}</option>
                <option value="MAINTENANCE">{t('cmms.asset_maintenance')}</option>
                <option value="DOWN">{t('cmms.asset_down')}</option>
                <option value="RETIRED">{t('cmms.asset_retired')}</option>
              </select>
            </div>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <label className="block text-sm font-medium text-green-900 mb-2">
              <MapPin className="w-4 h-4 inline mr-2" />
              {t('cmms.asset_assetLocation')}
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-green-700 mb-1">{t('cmms.asset_selectBranch')} *</label>
                <select
                  value={formData.branchId}
                  onChange={(e) => setFormData({...formData, branchId: e.target.value, locationId: ''})}
                  className="w-full px-3 py-2 border border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 bg-white"
                >
                  <option value="">{t('cmms.asset_selectBranch')}</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name} ({branch.code})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-green-700 mb-1">{t('cmms.asset_location')}</label>
                <select
                  value={formData.locationId}
                  onChange={(e) => setFormData({...formData, locationId: e.target.value})}
                  className="w-full px-3 py-2 border border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 bg-white"
                  disabled={!formData.branchId}
                >
                  <option value="">{t('cmms.asset_selectLocation')}</option>
                  {getFilteredLocations().map(location => (
                    <option key={location.id} value={location.id}>
                      {location.name} ({location.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-xs text-green-600 mt-2">
              Select where this asset is physically located
            </p>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <label className="block text-sm font-medium text-blue-900 mb-2">
              <Building2 className="w-4 h-4 inline mr-2" />
              {t('cmms.asset_supplierPurchasedFrom')}
            </label>
            <select
              value={showNewSupplierForm ? '__new__' : formData.supplierId}
              onChange={handleSupplierChange}
              className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">{t('cmms.asset_selectSupplier')}</option>
              {suppliers.map(supplier => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name} ({supplier.code})
                </option>
              ))}
              <option value="__new__">+ Add New Supplier</option>
            </select>
            {showNewSupplierForm && (
              <div className="mt-3 p-3 bg-white border border-blue-200 rounded-lg space-y-2">
                <p className="text-xs font-medium text-blue-800 mb-2">New Supplier</p>
                <input
                  type="text"
                  placeholder="Supplier name *"
                  value={newSupplier.name}
                  onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  placeholder="Contact person"
                  value={newSupplier.contactPerson}
                  onChange={(e) => setNewSupplier({ ...newSupplier, contactPerson: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="email"
                    placeholder="Email"
                    value={newSupplier.email}
                    onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    placeholder="Phone"
                    value={newSupplier.phone}
                    onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleCreateSupplier}
                    disabled={savingSupplier || !newSupplier.name.trim()}
                    className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                  >
                    {savingSupplier ? 'Saving...' : 'Save Supplier'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowNewSupplierForm(false); setNewSupplier({ name: '', contactPerson: '', email: '', phone: '' }); }}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            <p className="text-xs text-blue-600 mt-1">
              Select the vendor who supplied this asset
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.asset_manufacturer')}</label>
              <input
                type="text"
                value={formData.manufacturer}
                onChange={(e) => setFormData({...formData, manufacturer: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.asset_model')}</label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => setFormData({...formData, model: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.asset_serialNumber')}</label>
              <input
                type="text"
                value={formData.serialNumber}
                onChange={(e) => setFormData({...formData, serialNumber: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.asset_purchaseDate')}</label>
              <input
                type="date"
                value={formData.purchaseDate}
                onChange={(e) => setFormData({...formData, purchaseDate: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.asset_purchaseCost')}</label>
              <input
                type="number"
                value={formData.purchaseCost}
                onChange={(e) => setFormData({...formData, purchaseCost: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.asset_warrantyExpiry')}</label>
              <input
                type="date"
                value={formData.warrantyExpiration}
                onChange={(e) => setFormData({...formData, warrantyExpiration: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.asset_warrantyProvider')}</label>
              <input
                type="text"
                value={formData.warrantyProvider}
                onChange={(e) => setFormData({...formData, warrantyProvider: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.notes')}</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </OverageConsentModal>
    </div>
  );
};

export default Assets;
